// Recovery, and the hand-back that closes the case.
//
// THE SCORE decides when the patient may leave: Modified Aldrete, five
// parameters each 0-2, out of 10, dischargeable at 9. An incomplete score shows
// as incomplete rather than as a low total — a patient with three parameters
// recorded has not been assessed, and a number there would look like a judgement
// nobody made.
//
// Check-out routes the patient back to the held ward bed or on to ICU through
// the existing bed machinery, and closes the case. A bed problem is reported as
// a warning, never as a failure: the patient physically left recovery, and
// refusing to record that would leave the board lying about where they are.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import {
  addRecoveryOrder,
  fetchRecovery,
  markReadyForTransfer,
  receiveInRecovery,
  recordAldrete,
  recoveryCheckout,
} from "../../api/otApi";
import { DisabledHint, Field, FieldGrid, OtSelect, errorMessage, report } from "../common";
import { VitalsDialog } from "./FlowPanel";
import { ALDRETE_DISCHARGEABLE_AT, ALDRETE_PARAMS, RECOVERY_DESTINATIONS } from "../../constants";
import { vitalsLine } from "../../utils/vitalsLine";

/**
 * The pain score, beside the number that decides discharge.
 *
 * The Modified Aldrete has five parameters and none of them is pain, so a
 * patient can score 10 of 10 and be in agony. The score was already being
 * recorded -- it was the last item of a run-on observation line -- and nothing
 * ever put it where the discharge decision is made.
 *
 * THE TREND IS THE POINT. "6 then 8 then 9" and "9 then 8 then 6" are the same
 * latest number and opposite clinical pictures, so the run is shown, oldest
 * first, and the direction is stated in words rather than left to be read off
 * the digits.
 */
function PainCard({ pain }) {
  if (!pain || pain.latest === null || pain.latest === undefined) {
    return (
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 1,
          border: `1px dashed ${IPD_COLORS.border}`,
          fontSize: 13,
          color: IPD_COLORS.textSecondary,
        }}
      >
        No visual analog / numeric pain scale recorded in PACU. Document pain intensity score during post-op recovery assessment.
      </Box>
    );
  }

  const trend = pain.trend || [];
  const first = trend.length > 1 ? trend[0].score : null;
  const direction =
    first === null
      ? null
      : pain.latest > first
      ? "worsening"
      : pain.latest < first
      ? "settling"
      : "unchanged";

  return (
    <Box
      sx={{
        mt: 2.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: pain.high ? IPD_COLORS.warning.bg : IPD_COLORS.success.bg,
        color: pain.high ? IPD_COLORS.warning.text : IPD_COLORS.success.text,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
        Pain Score: {pain.latest} / {pain.max}
        {pain.high ? ` — Elevated (≥ ${pain.warn_at})` : ""}
      </Typography>
      <Typography sx={{ fontSize: 12.5, mt: 0.25 }}>
        {pain.high
          ? `Elevated pain score (≥${pain.warn_at}). Ensure analgesic titration and explicit verbal/written clinical handover to receiving ward nurse.`
          : "Controlled post-operative pain within target recovery range."}
      </Typography>

      {trend.length > 1 && (
        <Box sx={{ mt: 1, display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
          {trend.map((t, i) => (
            <Box
              key={`${t.at}-${i}`}
              title={`${formatIstDateTime(t.at)}${t.by ? ` — ${t.by}` : ""}`}
              sx={{
                px: 0.9,
                py: 0.2,
                borderRadius: 0.75,
                fontSize: 12,
                fontWeight: i === trend.length - 1 ? 800 : 600,
                border: `1px solid currentColor`,
                opacity: i === trend.length - 1 ? 1 : 0.6,
              }}
            >
              {t.score}
            </Box>
          ))}
          {direction && (
            <Typography sx={{ fontSize: 12, ml: 0.5 }}>{direction}</Typography>
          )}
        </Box>
      )}

      {pain.latest_at && (
        <Typography sx={{ fontSize: 11.5, mt: 0.75, opacity: 0.85 }}>
          Last recorded {formatIstDateTime(pain.latest_at)}
        </Typography>
      )}
    </Box>
  );
}

export default function RecoveryPanel({ otCase, onChanged, active = true }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [checkout, setCheckout] = useState({ destination: "WARD", destination_bed_code: "" });

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await fetchRecovery(otCase.id));
    } catch (err) {
      setError(errorMessage(err, "Could not load the recovery record"));
    }
  }, [otCase.id]);

  // Every case tab stays mounted, so what it read once goes stale when another
  // tab changes the case (consent, checklist, notes): re-read on opening.
  useEffect(() => {
    if (active) load();
  }, [active, load]);

  // The case moves when the patient is received into recovery, marked ready,
  // or checked out. Scoring them does not move it, so scoring re-reads this
  // one payload and leaves the parent alone — five parameters used to mean
  // five case reloads.
  const refreshAll = async () => {
    await load();
    onChanged();
  };

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;

  const recovery = data?.recovery;

  if (!recovery) {
    return (
      <IpdSectionCard title="Post-Anesthesia Care Unit (PACU) Admission">
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          {otCase.lifecycle_status === "IN_RECOVERY"
            ? "Patient has departed the operating theatre. Acknowledge PACU reception to initiate post-operative recovery monitoring."
            : "Patient is currently intra-operative; awaiting theatre discharge."}
        </Typography>
        <DisabledHint
          reason={
            otCase.lifecycle_status !== "IN_RECOVERY" &&
            "The patient has not left the operating theatre yet. Complete theatre checkout first."
          }
        >
          <IpdButton
            disabled={otCase.lifecycle_status !== "IN_RECOVERY"}
            onClick={async () => {
              if (report(await receiveInRecovery(otCase.id, {}))) refreshAll();
            }}
          >
            Confirm PACU Patient Reception
          </IpdButton>
        </DisabledHint>
      </IpdSectionCard>
    );
  }

  const total = recovery.aldrete_total;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <IpdSectionCard title="PACU Reception & Clinical Handover">
        <FieldGrid columns={4}>
          <Field label="PACU Admission Time" value={formatIstDateTime(recovery.arrived_at)} />
          <Field label="Handover Given By" value={recovery.handover_from} />
          <Field label="Post-Anesthetic Recovery State" value={recovery.anaesthesia_state} />
          <Field label="PACU Discharge Time" value={formatIstDateTime(recovery.departed_at)} />
          <Field label="Perioperative Clinical Handover Notes" value={recovery.handover_notes} span={4} />
        </FieldGrid>
      </IpdSectionCard>

      <IpdSectionCard
        title="Modified Aldrete Recovery Scoring"
        subtitle={`Five physiological dimensions (Activity, Respiration, Circulation, Consciousness, O2 Saturation; 0-2 points each). Discharge readiness criteria: Score ≥ ${ALDRETE_DISCHARGEABLE_AT}/10.`}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
            gap: 2,
          }}
        >
          {ALDRETE_PARAMS.map((p) => (
            <IpdFormField key={p.key} label={p.label}>
              <OtSelect
                placeholder="—"
                // The five parameters are nested under `aldrete`, not flat on
                // the row. Read flat they were always undefined, so a scored
                // patient's selects came up blank and the score looked lost.
                value={recovery.aldrete?.[p.key] ?? ""}
                onChange={async (e) => {
                  const res = await recordAldrete(otCase.id, { [p.key]: e.target.value });
                  // The score's own message is worth reading — it says whether
                  // the patient has become dischargeable — so this one is not
                  // quiet.
                  if (report(res)) setData((c) => ({ ...c, recovery: res.data }));
                }}
                options={[
                  { value: 0, label: "0" },
                  { value: 1, label: "1" },
                  { value: 2, label: "2" },
                ]}
              />
            </IpdFormField>
          ))}

          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: recovery.dischargeable ? IPD_COLORS.success.bg : IPD_COLORS.warning.bg,
              color: recovery.dischargeable ? IPD_COLORS.success.text : IPD_COLORS.warning.text,
              fontWeight: 700,
              fontSize: 13.5,
            }}
          >
            {total === null || total === undefined
              ? "Five-dimension physiological assessment incomplete. Continue monitoring."
              : recovery.dischargeable
              ? `${total} / 10 — Meets clinical discharge criteria (Aldrete Score ≥ ${ALDRETE_DISCHARGEABLE_AT}). Authorized for transfer to receiving unit.`
              : `${total} / 10 — Below discharge threshold (< ${ALDRETE_DISCHARGEABLE_AT}). Continue PACU monitoring and supportive therapy.`}
          </Box>
          {recovery.scored_at && (
            <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
              Last scored {formatIstDateTime(recovery.scored_at)} by {recovery.scored_by_name || "—"}
            </Typography>
          )}

          <PainCard pain={recovery.pain} />
        </Box>
      </IpdSectionCard>

      <IpdSectionCard
        title="PACU Vital Signs & Recovery Observations"
        actions={
          <Button size="small" variant="contained" onClick={() => setVitalsOpen(true)}>
            Record Observations
          </Button>
        }
      >
        {(data.vitals || []).length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            No vital signs or recovery observations logged yet.
          </Typography>
        ) : (
          data.vitals.map((v) => (
            <Typography
              key={v.id}
              sx={{ fontSize: 13, mb: 0.5, borderBottom: `1px solid ${IPD_COLORS.border}`, pb: 0.5 }}
            >
              {formatIstDateTime(v.recorded_at)} — {vitalsLine(v)}
            </Typography>
          ))
        )}
      </IpdSectionCard>

      <IpdSectionCard
        title="Post-Operative Nursing Directives & Ward Handover Orders"
        subtitle="Special surgical post-op directives and nursing care instructions for the receiving inpatient ward."
      >
        {(data.orders || []).map((o) => (
          <Typography key={o.id} sx={{ fontSize: 13, py: 0.4 }}>
            • {o.instruction}
            {o.category ? ` (${o.category})` : ""} —{" "}
            <span style={{ color: IPD_COLORS.textSecondary }}>
              {o.recorded_by_name} {formatIstDateTime(o.recorded_at)}
            </span>
          </Typography>
        ))}
        <Box sx={{ mt: 2, display: "flex", gap: 1.5, alignItems: "flex-end" }}>
          <Box sx={{ flex: 1 }}>
            <IpdFormField label="Post-Operative Clinical Instruction / Directive">
              <IpdTextField
                fullWidth
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
              />
            </IpdFormField>
          </Box>
          <IpdButton
            disabled={!instruction.trim()}
            onClick={async () => {
              if (report(await addRecoveryOrder(otCase.id, { instruction }))) {
                setInstruction("");
                await load();
              }
            }}
          >
            Add Directive
          </IpdButton>
        </Box>
      </IpdSectionCard>

      <IpdSectionCard
        title="PACU Discharge & Inpatient Ward Handover"
        subtitle="Patient transfer out of PACU to inpatient nursing unit, ICU, or daycare discharge."
      >
        {recovery.departed_at ? (
          <FieldGrid columns={3}>
            <Field label="Transfer Destination Unit" value={recovery.destination} />
            <Field label="Allocated Inpatient Bed" value={recovery.destination_bed_label} />
            <Field label="Discharge Authorized By" value={recovery.checked_out_by_name} />
            {recovery.bed_action_error && (
              <Field label="Bed Allocation Warning" value={recovery.bed_action_error} span={3} />
            )}
          </FieldGrid>
        ) : (
          <>
            <Box sx={{ mb: 2.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <IpdFormField label="Discharge Transfer Destination">
                <OtSelect
                  value={checkout.destination}
                  onChange={(e) =>
                    setCheckout((c) => ({ ...c, destination: e.target.value }))
                  }
                  options={RECOVERY_DESTINATIONS}
                />
              </IpdFormField>
              {checkout.destination === "ICU" && (
                <IpdFormField label="Designated ICU Bed Identifier">
                  <IpdTextField
                    fullWidth
                    value={checkout.destination_bed_code}
                    onChange={(e) =>
                      setCheckout((c) => ({ ...c, destination_bed_code: e.target.value }))
                    }
                  />
                </IpdFormField>
              )}
            </Box>

            <DisabledHint
              reason={
                recovery.ready_for_transfer
                  ? "Transfer authorization is already active for this case."
                  : !recovery.dischargeable && checkout.destination !== "ICU"
                  ? `Ward transfer requires an Aldrete recovery score of at least ${ALDRETE_DISCHARGEABLE_AT}/10. Record a qualifying score, or select ICU as the destination (ICU is exempt).`
                  : ""
              }
            >
            <Button
              size="small"
              variant="outlined"
              disabled={recovery.ready_for_transfer || (!recovery.dischargeable && checkout.destination !== "ICU")}
              onClick={async () => {
                if (report(await markReadyForTransfer(otCase.id, { destination: checkout.destination }))) refreshAll();
              }}
            >
              {recovery.ready_for_transfer
                ? "Transfer Authorization Active"
                : checkout.destination === "ICU"
                ? "Authorize Direct Transfer to ICU"
                : "Authorize Ready for Ward Transfer"}
            </Button>
            </DisabledHint>

            <Box sx={{ mt: 2.5 }}>
              <DisabledHint
                reason={
                  !recovery.ready_for_transfer &&
                  "Authorize the transfer above before closing the surgical episode."
                }
              >
                <IpdButton
                  disabled={!recovery.ready_for_transfer}
                  onClick={async () => {
                    if (report(await recoveryCheckout(otCase.id, checkout))) refreshAll();
                  }}
                >
                  Complete PACU Discharge & Close Surgical Episode
                </IpdButton>
              </DisabledHint>
              {!recovery.ready_for_transfer && checkout.destination !== "ICU" && (
                <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  Ward transfer authorization requires an Aldrete recovery score of at least {ALDRETE_DISCHARGEABLE_AT}/10. (ICU destination exempt).
                </Typography>
              )}
              {recovery.pain?.high && (
                <Typography sx={{ mt: 1, fontSize: 12.5, color: IPD_COLORS.warning.text }}>
                  Elevated pain score ({recovery.pain.latest}/{recovery.pain.max}). Ensure post-operative analgesia orders and verbal clinical handover to the receiving ward nurse.
                </Typography>
              )}
            </Box>
          </>
        )}
      </IpdSectionCard>

      <VitalsDialog
        open={vitalsOpen}
        caseId={otCase.id}
        context="RECOVERY"
        onClose={() => setVitalsOpen(false)}
        onDone={() => {
          setVitalsOpen(false);
          load();
        }}
      />
    </Box>
  );
}
